import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useEndAgenticSession } from '@components/Copilot/hooks';
import { AgentId, ChatMessage, TAgentState, TCopilotError, TSessionProps, UserPrompt } from '@components/Copilot/types';
import { useAgenticConversationSSE as useAgenticConversation } from '@components/Copilot/useAgentSSE';
import { asyncNoop, noop, QueryKey } from '@fabric-ui/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useOnSubNavigate } from '@utils/AvRouter';

const defaultLastSessionProps: TSessionProps = {
  agentId: AgentId.REMEDIATION_COPILOT,
  additionalParams: {
    entityKey: '',
    projectionId: { name: '', builtIn: true },
  },
  actions: [],
  getConfirmDialogConfig: undefined,
};

export interface IAvCopilotContext {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sessionId: string;
  setSessionId: React.Dispatch<React.SetStateAction<string>>;
  restartCopilotSession: () => Promise<void>;
  endCopilotSession: () => void;
  forceDiscardCopilot: (keepSession?: boolean) => void;
  closeCopilotWithBlocker: () => void;
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  postMessage: (message: UserPrompt) => void;
  isPollingEnabled: boolean;
  conversationErrorMessage: TCopilotError | null;
  setConversationErrorMessage: React.Dispatch<React.SetStateAction<TCopilotError | null>>;
  onOpenCopilot: (props: TSessionProps) => void;
  startSessionWithoutUI: (props: TSessionProps) => void;
  lastSessionProps: TSessionProps;
  isDirty: boolean;
  isNavigationBlockerOpen: boolean;
  setIsNavigationBlockerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDefaultPromptsOpen: boolean;
  setIsDefaultPromptsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showActionButtons: boolean;
  setShowActionButtons: React.Dispatch<React.SetStateAction<boolean>>;
  excludeStateUpdate: boolean;
  setExcludeStateUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  stateRef: React.RefObject<{ currentState: TAgentState; lastKnownState: TAgentState }>;
  thinkingText: string;
  onCustomOperationSuccessRef: React.MutableRefObject<((data: Record<string, any>) => void) | null>;
  isReducedContextMode: boolean;
}

export const AvCopilotContext = React.createContext<IAvCopilotContext>({
  isOpen: false,
  setIsOpen: noop,
  sessionId: '',
  setSessionId: noop,
  restartCopilotSession: asyncNoop,
  endCopilotSession: noop,
  forceDiscardCopilot: noop,
  closeCopilotWithBlocker: noop,
  messages: [],
  addMessage: noop,
  postMessage: noop,
  isPollingEnabled: false,
  conversationErrorMessage: null,
  setConversationErrorMessage: noop,
  onOpenCopilot: noop,
  startSessionWithoutUI: noop,
  lastSessionProps: defaultLastSessionProps,
  isDirty: false,
  isNavigationBlockerOpen: false,
  setIsNavigationBlockerOpen: noop,
  isDefaultPromptsOpen: false,
  setIsDefaultPromptsOpen: noop,
  showActionButtons: true,
  setShowActionButtons: noop,
  excludeStateUpdate: true,
  setExcludeStateUpdate: noop,
  stateRef: { current: { currentState: undefined, lastKnownState: undefined } },
  thinkingText: 'Thinking',
  onCustomOperationSuccessRef: { current: null },
  isReducedContextMode: false,
});

export function AvCopilotContextProvider({ children }: React.PropsWithChildren = {}) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isNavigationBlockerOpen, setIsNavigationBlockerOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationErrorMessage, setConversationErrorMessage] = useState<TCopilotError | null>(null);
  const [lastSessionProps, setLastSessionProps] = useState<TSessionProps>(defaultLastSessionProps);
  const [isDefaultPromptsOpen, setIsDefaultPromptsOpen] = useState<boolean>(false);
  const [showActionButtons, setShowActionButtons] = useState<boolean>(true);
  const [excludeStateUpdate, setExcludeStateUpdate] = useState<boolean>(true);
  const [isHeadlessSession, setIsHeadlessSession] = useState<boolean>(false);
  const stateRef = useRef<{ currentState: TAgentState; lastKnownState: TAgentState }>({
    currentState: undefined,
    lastKnownState: undefined,
  });

  const isDirty = useMemo(
    () => messages.length > 1 && !!lastSessionProps.actions.length && !!lastSessionProps.getConfirmDialogConfig,
    [lastSessionProps.actions.length, lastSessionProps.getConfirmDialogConfig, messages.length]
  );

  const { mutate: endSession } = useEndAgenticSession();

  const resetCopilotState = useCallback(() => {
    setSessionId('');
    setMessages([]);
    setConversationErrorMessage(null);
    setExcludeStateUpdate(true);
    stateRef.current = { currentState: undefined, lastKnownState: undefined };
  }, []);

  const endCopilotSession = useCallback(() => {
    if (lastSessionProps.retainSessionOnClose) {
      return;
    }
    if (sessionId) {
      resetCopilotState();
      endSession({ sessionId });
    }
  }, [sessionId, resetCopilotState, endSession, lastSessionProps.retainSessionOnClose]);

  const onOpenCopilot = (sessionProps: TSessionProps) => {
    setIsOpen(true);
    setIsHeadlessSession(false);
    setLastSessionProps(sessionProps);
    if (sessionProps.retainSessionOnClose) {
      initBasePathToRetain();
    }

    if (sessionId) {
      endCopilotSession();
    } else if (lastSessionProps) {
      resetCopilotState();
    }
  };

  const startSessionWithoutUI = (sessionProps: TSessionProps) => {
    setIsHeadlessSession(true);
    setLastSessionProps(sessionProps);
    if (sessionProps.retainSessionOnClose) {
      initBasePathToRetain();
    }

    if (sessionId) {
      endCopilotSession();
    } else if (lastSessionProps) {
      resetCopilotState();
    }
  };

  const addMessage = useCallback((newMessage: ChatMessage) => setMessages(prevMessages => [...prevMessages, newMessage]), [setMessages]);

  const agenticConversation = useAgenticConversation({
    isCopilotRunning: isOpen || isHeadlessSession,
    sessionId,
    addMessage,
    setErrorMessage: setConversationErrorMessage,
    setSessionId,
    lastSessionProps,
    excludeStateUpdate,
    stateRef,
  });

  const restartCopilotSession = useCallback(async () => {
    if (sessionId) {
      endCopilotSession();
    }
    resetCopilotState();
    if (!sessionId) {
      agenticConversation.triggerStart();
    }
  }, [sessionId, endCopilotSession, resetCopilotState, queryClient, agenticConversation.triggerStart]);

  const forceDiscardCopilot = useCallback(
    (keepSession = lastSessionProps.retainSessionOnClose) => {
      setIsOpen(false);
      if (keepSession) {
        return;
      }
      if (lastSessionProps.retainSessionOnClose) {
        setLastSessionProps(prev => ({ ...prev, retainSessionOnClose: false }));
      }
      resetCopilotState();
      queryClient.removeQueries({ queryKey: [QueryKey.Copilot] });
      if (sessionId) {
        endSession({ sessionId });
      }
    },
    [endSession, resetCopilotState, sessionId, lastSessionProps.retainSessionOnClose]
  );

  const initBasePathToRetain = useOnSubNavigate(forceDiscardCopilot, lastSessionProps.retainSessionOnClose);

  const closeCopilotWithBlocker = useCallback(() => {
    if (isDirty) {
      setIsNavigationBlockerOpen(true);
    } else {
      forceDiscardCopilot();
    }
  }, [isDirty, forceDiscardCopilot]);

  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      sessionId,
      setSessionId,
      restartCopilotSession,
      endCopilotSession,
      forceDiscardCopilot,
      closeCopilotWithBlocker,
      messages,
      addMessage,
      postMessage: agenticConversation.postMessage,
      isPollingEnabled: agenticConversation.isPollingEnabled,
      thinkingText: agenticConversation.thinkingText ?? 'Thinking',
      conversationErrorMessage,
      setConversationErrorMessage,
      onOpenCopilot,
      startSessionWithoutUI,
      lastSessionProps,
      isDirty,
      isNavigationBlockerOpen,
      setIsNavigationBlockerOpen,
      isDefaultPromptsOpen,
      setIsDefaultPromptsOpen,
      showActionButtons,
      setShowActionButtons,
      excludeStateUpdate,
      setExcludeStateUpdate,
      stateRef,
      onCustomOperationSuccessRef: agenticConversation.onCustomOperationSuccessRef,
      isReducedContextMode: agenticConversation.isReducedContextMode ?? false,
    }),
    [
      isOpen,
      setIsOpen,
      sessionId,
      setSessionId,
      restartCopilotSession,
      endCopilotSession,
      forceDiscardCopilot,
      closeCopilotWithBlocker,
      messages,
      addMessage,
      agenticConversation.postMessage,
      agenticConversation.isPollingEnabled,
      agenticConversation.thinkingText,
      conversationErrorMessage,
      setConversationErrorMessage,
      onOpenCopilot,
      lastSessionProps,
      isDirty,
      isNavigationBlockerOpen,
      setIsNavigationBlockerOpen,
      isDefaultPromptsOpen,
      setIsDefaultPromptsOpen,
      showActionButtons,
      setShowActionButtons,
      excludeStateUpdate,
      setExcludeStateUpdate,
      agenticConversation.isReducedContextMode,
    ]
  );

  return <AvCopilotContext.Provider value={value}>{children}</AvCopilotContext.Provider>;
}

export const useAvCopilotContext = () => useContext(AvCopilotContext);
